import { OverlayModule } from '@angular/cdk/overlay';
import { DatePipe, DecimalPipe, NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import {
  GeoJSONSourceComponent,
  LayerComponent,
  MapComponent,
  MarkerComponent,
  PopupComponent,
} from '@maplibre/ngx-maplibre-gl';
import { DialogComponent, MapAntennaMarkerComponent, SwitchComponent } from '@wheere-front/shared-ui';
import { mapBuilding } from '@wheere-front/shared-util';
import { Gateway } from '@wheere/api-site';
import { Position } from '@wheere/api-tracker';
import type { Feature, FeatureCollection, LineString, Point } from 'geojson';
import { Map, MapMouseEvent } from 'maplibre-gl';
import { HISTORY_POINTS_PAINT } from './history-points-paint';
import { AnimationStateService } from '../../util/animation-state.service';
import { ANIMATED_POINT_LAYER, MapAnimationPointService } from '../../util/map-animation-point.service';
import { MapStateService } from '../../util/map-state.service';
import { PositionWithColor } from '../../util/position-with-color';
import { REPLAY_SITE_BUILDING, ReplaySiteBuilding } from '../../util/replay-site-building.token';
import { MapReadControlsComponent } from './map-read-controls.component';

// Re-export for any consumer that imported PositionWithColor from this file before the
// type moved to ../../util/position-with-color.
export { PositionWithColor };

const POINTS_SOURCE = 'positions';
const POINTS_LAYER = 'unclustered-points';
const LINE_SOURCE = 'positions-line';
const LINE_LAYER = 'positions-line';
/** Trajet et points restent au sol, dessinés par-dessus le bâtiment 3D (décision du 2026-10-29). */
const OVER_BUILDING = [LINE_LAYER, POINTS_LAYER, ANIMATED_POINT_LAYER] as const;

/**
 * Full-screen replay of one tracker's positions over a period: the path and every point are
 * drawn as soon as the map loads (colour = time, blue → yellow), a point is selected by
 * clicking it, and the bottom console drives playback and the time range.
 */

@Component({
  selector: 'lib-map-read-positions',
  standalone: true,
  imports: [
    OverlayModule,
    DatePipe,
    DecimalPipe,
    NgComponentOutlet,
    MapComponent,
    LayerComponent,
    MarkerComponent,
    PopupComponent,
    SwitchComponent,
    FormsModule,
    TranslocoModule,
    MapReadControlsComponent,
    GeoJSONSourceComponent,
    MapAntennaMarkerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="relative h-screen w-screen overflow-hidden bg-gray-100 xl:rounded-lg xl:shadow-lg dark:bg-slate-950">
      @defer {
        <mgl-map
          [mapStyle]="mapStyle()"
          [fitBounds]="fitBounds()"
          [fitBoundsOptions]="{ padding: { top: 88, bottom: 176, left: 32, right: 32 } }"
          [maxZoom]="19"
          (mapLoad)="onMapLoad($event)"
          class="h-full w-full">
          @if (map(); as map) {
            <mgl-geojson-source id="source" [data]="outlineData()"></mgl-geojson-source>
            <mgl-layer
              id="level-fill"
              source="source"
              type="fill"
              [paint]="{ 'fill-color': 'black', 'fill-opacity': 0.2 }">
            </mgl-layer>
            <mgl-layer
              id="level-line"
              source="source"
              type="line"
              [paint]="{ 'line-color': 'black', 'line-opacity': 0.8, 'line-width': 2 }">
            </mgl-layer>

            <!-- V8 unified topology — antennas are the reception points everywhere. -->
            @for (a of flatAntennas(); track a.id) {
              <lib-map-antenna-marker
                [antenna]="a.antenna"
                [gatewayName]="a.gatewayName"
                [offset]="
                  building?.offset(a.antenna.latitude, a.antenna.longitude, a.antenna.altitude) ?? null
                "></lib-map-antenna-marker>
            }

            <!-- Selected point: ring marker + card -->
            @if (selectedPosition(); as sel) {
              <mgl-marker [lngLat]="{ lng: sel.longitude!, lat: sel.latitude! }">
                <span
                  class="block size-5 rounded-full border-[3px] border-white shadow-md ring-2 ring-gray-900/60 dark:ring-white/60"
                  [style.background]="sel.color"></span>
              </mgl-marker>
              <!-- Standalone popup: a marker-bound one only opens on a second click on the marker. -->
              <mgl-popup
                [lngLat]="{ lng: sel.longitude!, lat: sel.latitude! }"
                [offset]="18"
                [closeButton]="false"
                [closeOnClick]="false"
                [maxWidth]="'280px'">
                <div class="min-w-56 text-gray-900 dark:text-slate-100">
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-sm font-semibold tabular-nums">{{
                      sel.timestamp | date: 'dd/MM/yyyy HH:mm:ss'
                    }}</span>
                    <span class="text-xs text-gray-500 dark:text-slate-400"
                      >{{ selectedIndex() + 1 }} / {{ positionsWithColor().length }}</span
                    >
                  </div>
                  <div class="mt-1 text-xs tabular-nums text-gray-600 dark:text-slate-300">
                    {{ sel.latitude | number: '1.6-6' }}, {{ sel.longitude | number: '1.6-6' }}
                    @if (sel.altitude !== null && sel.altitude !== undefined && isFinite(sel.altitude)) {
                      <span class="text-gray-400 dark:text-slate-500"> · {{ sel.altitude | number: '1.0-1' }} m</span>
                    }
                  </div>
                  <div class="mt-2 flex items-center justify-between">
                    <button
                      type="button"
                      class="popup-btn"
                      [disabled]="selectedIndex() <= 0"
                      (click)="selectNeighbour(-1)">
                      <i class="pi pi-angle-left"></i> {{ 'positionReplay.previous' | transloco }}
                    </button>
                    <button type="button" class="popup-btn" (click)="selectedPosition.set(null)">
                      {{ 'positionReplay.deselect' | transloco }}
                    </button>
                    <button
                      type="button"
                      class="popup-btn"
                      [disabled]="selectedIndex() >= positionsWithColor().length - 1"
                      (click)="selectNeighbour(1)">
                      {{ 'positionReplay.next' | transloco }} <i class="pi pi-angle-right"></i>
                    </button>
                  </div>
                </div>
              </mgl-popup>
            }
          }
        </mgl-map>
      }

      <!-- Overlay chrome: info (top-left), map style + close (top-right), console (bottom) -->
      <div class="pointer-events-none absolute inset-0 flex flex-col justify-between p-3 sm:p-4">
        <div class="flex items-start justify-between gap-3">
          <div
            class="pointer-events-auto rounded-2xl border border-black/5 bg-white/95 px-4 py-3 text-gray-900 shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-slate-900/95 dark:text-slate-100">
            <h2 class="text-base font-semibold leading-tight">{{ 'positionReplay.title' | transloco }}</h2>
            <p class="mt-0.5 text-sm text-gray-600 dark:text-slate-300">
              {{ 'positionReplay.positions' | transloco: { count: positionsWithColor().length } }}
              @if (period(); as pr) {
                <span class="tabular-nums">
                  · {{ pr.start | date: 'dd/MM/yy HH:mm' }} → {{ pr.end | date: 'dd/MM/yy HH:mm' }}
                </span>
              }
            </p>
            @if (positionsWithColor().length === 0) {
              <p class="mt-1 text-xs text-amber-700 dark:text-amber-300">
                {{ 'positionReplay.noPositions' | transloco }}
              </p>
            }
            <!-- Bâtiment du site (inside, ADR-017) : contours GeoJSON par niveau ou modèle 3D. -->
            @if (building) {
              <div class="mt-3 border-t border-black/5 pt-3 dark:border-white/10">
                <ng-container *ngComponentOutlet="buildingControls; inputs: { building }" />
                @if (!building.active()) {
                  <div
                    class="flex flex-wrap items-center gap-1.5"
                    role="group"
                    [attr.aria-label]="'positionReplay.outlineLevel' | transloco">
                    <span class="mr-0.5 text-xs font-semibold text-gray-500 dark:text-slate-400">{{
                      'positionReplay.outlineLevel' | transloco
                    }}</span>
                    @for (l of outlineLevelChoices; track l) {
                      <button
                        type="button"
                        class="h-7 rounded px-2.5 text-xs font-bold"
                        [class]="
                          outlineLevel() === l
                            ? 'bg-gray-900 text-white dark:bg-slate-100 dark:text-slate-900'
                            : 'border border-gray-200 text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700'
                        "
                        [attr.aria-pressed]="outlineLevel() === l"
                        (click)="outlineLevel.set(l)">
                        {{ l ?? ('positionReplay.noOutline' | transloco) }}
                      </button>
                    }
                  </div>
                }
              </div>
            }
          </div>
          <div class="pointer-events-auto flex items-center gap-2">
            @if (map()) {
              <label
                class="flex cursor-pointer items-center gap-2 rounded-full border border-black/5 bg-white/95 py-1.5 pl-3 pr-2 text-xs text-gray-700 shadow-lg backdrop-blur-md dark:border-white/10 dark:bg-slate-900/95 dark:text-slate-200">
                {{ 'positionReplay.satellite' | transloco }}
                <lib-switch
                  size="sm"
                  [checked]="mapStyleType() !== 'street'"
                  (changed)="onToggleMapStyle()"></lib-switch>
              </label>
            }
            <button
              type="button"
              class="focus-visible:ring-primary-300 inline-flex size-10 items-center justify-center rounded-full border border-black/5 bg-white/95 text-gray-700 shadow-lg backdrop-blur-md transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 dark:border-white/10 dark:bg-slate-900/95 dark:text-slate-200 dark:hover:bg-slate-800"
              [attr.aria-label]="'positionReplay.close' | transloco"
              [title]="'positionReplay.close' | transloco"
              (click)="closeDialog()">
              <i class="pi pi-times"></i>
            </button>
          </div>
        </div>

        @if (map()) {
          <div class="flex justify-center">
            <lib-map-read-controls
              [positionsWithColor]="positionsWithColor()"
              [allPositionsWithColor]="allPositionsWithColor()"
              [animationsStep]="animationsStep()"
              [speeds]="speeds"
              [showAllPositionsSignal]="showAllPositions()"
              (playPause)="playPauseAnimation()"
              (animationStop)="stopAnimation()"
              (setSpeed)="setSpeed($event)"
              (trackerSelected)="selectTracker($event)"
              (togglePositions)="togglePositions($event)"
              (timeRangeChanged)="onTimeRangeChanged($event)">
            </lib-map-read-controls>
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host ::ng-deep .maplibregl-popup-content {
        border-radius: 0.75rem;
        padding: 0.75rem 0.875rem;
        box-shadow: 0 10px 30px rgba(15, 23, 42, 0.25);
      }
      :host-context(.dark) ::ng-deep .maplibregl-popup-content {
        background: #0f172a;
      }
      :host-context(.dark) ::ng-deep .maplibregl-popup-tip {
        border-top-color: #0f172a;
        border-bottom-color: #0f172a;
      }
      .popup-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        border-radius: 9999px;
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
        color: #db2777;
      }
      .popup-btn:hover:not(:disabled) {
        background: rgba(237, 16, 125, 0.1);
      }
      .popup-btn:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }
      .popup-btn:focus-visible {
        outline: 2px solid #f9a8d4;
        outline-offset: 1px;
      }
    `,
  ],
})
export class MapPositionsReadHistoryComponent implements OnInit, OnDestroy {
  private mapStateService = inject(MapStateService);
  private animationStateService = inject(AnimationStateService);
  private mapAnimationService = inject(MapAnimationPointService);
  private destroyRef = inject(DestroyRef);

  trackerId = input.required<string>();
  positions = input.required<Position[]>();
  dialog = input.required<DialogComponent>();
  gateways = input<Gateway[]>();
  /** Site du tracker : son bâtiment 3D quand l'application l'active (inside). */
  siteId = input<string | null | undefined>(null);

  readonly #buildingFeature = inject(REPLAY_SITE_BUILDING, { optional: true });
  /** `null` hors inside : la carte garde son plan en contours au niveau 1, comme avant. */
  protected readonly building: ReplaySiteBuilding | null = this.#buildingFeature?.create() ?? null;
  protected readonly buildingControls = this.#buildingFeature?.controls ?? null;
  readonly #loadedMap = signal<Map | null>(null);

  /**
   * Flat antenna union across every gateway, sorted by (gateway.orderSite ×
   * antenna.orderGateway). V8 unified topology: antennas ARE the reception points everywhere.
   */
  protected readonly flatAntennas = computed<
    { id: string; antenna: NonNullable<Gateway['antennas']>[number]; gatewayName: string | undefined }[]
  >(() => {
    const gws = this.gateways();
    if (!gws?.length) return [];
    const out: { id: string; antenna: NonNullable<Gateway['antennas']>[number]; gatewayName: string | undefined }[] =
      [];
    const sortedGws = [...gws].sort((a, b) => (a.orderSite ?? 0) - (b.orderSite ?? 0));
    for (const g of sortedGws) {
      const antennas = [...(g.antennas ?? [])].sort((x, y) => (x.orderGateway ?? 0) - (y.orderGateway ?? 0));
      for (const a of antennas) {
        if (a.longitude == null || a.latitude == null) continue;
        out.push({ id: a.id ?? `${g.id}-${a.orderGateway}`, antenna: a, gatewayName: g.name ?? undefined });
      }
    }
    return out;
  });

  selectedPosition = signal<PositionWithColor | null>(null);
  timeFilter = signal<{ start: number; end: number } | null>(null);

  @ViewChild(MapReadControlsComponent) controlsComponent!: MapReadControlsComponent;

  animationsStep = signal(100);
  /** Points are visible by default: an empty map with nothing to see was the old first impression. */
  showAllPositions = signal(true);
  /** Replay speeds, as multipliers of the 100 ms base step. */
  speeds = [
    { label: '0.1×', value: 1000 },
    { label: '0.2×', value: 500 },
    { label: '1×', value: 100 },
    { label: '2×', value: 50 },
    { label: '10×', value: 10 },
  ];

  protected readonly isFinite = Number.isFinite;

  private features = mapBuilding.data.features.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (f: any) => f.properties?.indoor === 'room'
  );

  /** Niveaux du plan en contours ; `null` : aucun contour. */
  protected readonly outlineLevelChoices: (string | null)[] = [
    null,
    ...[
      ...new Set(
        this.features
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((f: any): unknown => f.properties?.level)
          .filter((l): l is string => typeof l === 'string')
      ),
    ].sort(),
  ];
  protected readonly outlineLevel = signal<string | null>('1');
  /** Plan en contours du niveau choisi ; vide quand le modèle 3D est affiché. */
  protected readonly outlineData = computed<GeoJSON.FeatureCollection>(() => {
    const level = this.outlineLevel();
    if (this.building?.active() || level === null) {
      return { type: 'FeatureCollection', features: [] };
    }
    return {
      ...mapBuilding.data,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      features: this.features.filter((f: any) => f.properties?.level === level),
    } as GeoJSON.FeatureCollection;
  });

  map = this.mapStateService.map;
  mapStyleType = this.mapStateService.mapStyleType;
  mapStyle = this.mapStateService.mapStyle;

  // All valid positions with colors (unfiltered), oldest first.
  allPositionsWithColor = computed<PositionWithColor[]>(() => {
    const validPositions = this.positions().filter(
      p =>
        p.latitude != null &&
        p.longitude != null &&
        p.latitude !== 0 &&
        p.longitude !== 0 &&
        !isNaN(p.latitude) &&
        !isNaN(p.longitude)
    );
    const sortedPositions = [...validPositions].sort((a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeA - timeB;
    });
    const total = sortedPositions.length;
    return sortedPositions.map((p, idx) => ({ ...p, color: this.generateColorFromIndex(idx, total) }));
  });

  // Positions inside the selected time range.
  positionsWithColor = computed<PositionWithColor[]>(() => {
    const all = this.allPositionsWithColor();
    const filter = this.timeFilter();
    if (!filter) return all;
    return all.filter(p => {
      if (!p.timestamp) return true;
      const time = new Date(p.timestamp).getTime();
      return time >= filter.start && time <= filter.end;
    });
  });

  protected readonly period = computed(() => {
    const list = this.positionsWithColor();
    const first = list.find(p => p.timestamp)?.timestamp;
    const last = [...list].reverse().find(p => p.timestamp)?.timestamp;
    return first && last ? { start: new Date(first), end: new Date(last) } : null;
  });

  protected readonly selectedIndex = computed(() => {
    const sel = this.selectedPosition();
    if (!sel) return -1;
    return this.positionsWithColor().findIndex(p => p.timestamp === sel.timestamp && p.latitude === sel.latitude);
  });

  fitBounds = computed<[[number, number], [number, number]]>(() => {
    const coords = this.positionsWithColor().map(p => [p.longitude!, p.latitude!] as [number, number]);
    if (coords.length) {
      const lats = coords.map(c => c[1]);
      const lngs = coords.map(c => c[0]);
      return [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ];
    }
    return [
      [2.2241, 48.8156],
      [2.4699, 48.9022],
    ];
  });

  /** Whether the points were shown before playback started — restored when it ends. */
  private pointsBeforePlay = true;

  constructor() {
    // Bâtiment chargé une fois par carte et par site ; un site sans modèle garde ses contours.
    let loadedFor: { map: Map; siteId: string } | null = null;
    effect(() => {
      const map = this.#loadedMap();
      const siteId = this.siteId();
      const building = this.building;
      if (!building || !map || !siteId || (loadedFor?.map === map && loadedFor.siteId === siteId)) {
        return;
      }
      loadedFor = { map, siteId };
      untracked(() => void building.load(map, siteId, OVER_BUILDING));
    });
  }

  ngOnInit() {
    this.mapAnimationService.animationCompleted.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.pointsBeforePlay) {
        this.togglePositions(true);
      }
    });
  }

  ngOnDestroy() {
    this.building?.detach();
    const map = this.map();
    if (!map) return;
    map.off('click', POINTS_LAYER, this.onPointClick);
    map.off('mouseenter', POINTS_LAYER, this.onPointEnter);
    map.off('mouseleave', POINTS_LAYER, this.onPointLeave);
  }

  onMapLoad(mapInstance: Map | undefined) {
    if (!mapInstance) {
      return;
    }
    this.mapStateService.map.set(mapInstance);
    this.#loadedMap.set(mapInstance);
    this.setupGeoJsonSource(mapInstance);
    if (this.showAllPositions()) {
      this.forceShowAllPositions();
    }
    mapInstance.on('click', POINTS_LAYER, this.onPointClick);
    mapInstance.on('mouseenter', POINTS_LAYER, this.onPointEnter);
    mapInstance.on('mouseleave', POINTS_LAYER, this.onPointLeave);
  }

  private readonly onPointClick = (e: MapMouseEvent & { features?: Feature[] }) => {
    const props = e.features?.[0]?.properties as PositionWithColor | undefined;
    if (!props) return;
    this.selectedPosition.set({ ...props });
  };
  private readonly onPointEnter = () => {
    const map = this.map();
    if (map) map.getCanvas().style.cursor = 'pointer';
  };
  private readonly onPointLeave = () => {
    const map = this.map();
    if (map) map.getCanvas().style.cursor = '';
  };

  onToggleMapStyle() {
    const selectedPoint = this.selectedPosition();
    const showingPositions = this.showAllPositions();

    this.mapStateService.onToggleMapStyle();

    setTimeout(() => {
      const map = this.map();
      if (!map) return;

      this.setupGeoJsonSource(map);

      if (showingPositions) {
        this.showAllPositions.set(false);
        this.togglePositions(true);
      }

      if (selectedPoint) {
        this.selectedPosition.set(selectedPoint);
      }

      this.mapAnimationService.handleMapStyleChange();
    }, 300);
  }

  /** (Re)build the point source and the path line for the positions in range. */
  setupGeoJsonSource(mapInstance: Map) {
    const list = this.positionsWithColor();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const features: Feature<Point, any>[] = list.map(p => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.longitude!, p.latitude!] },
      properties: { ...p },
    }));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const geojson: FeatureCollection<Point, any> = { type: 'FeatureCollection', features };
    this.upsertSource(mapInstance, POINTS_SOURCE, geojson);

    const line: FeatureCollection<LineString> = {
      type: 'FeatureCollection',
      features:
        list.length >= 2
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'LineString', coordinates: list.map(p => [p.longitude!, p.latitude!]) },
              },
            ]
          : [],
    };
    this.upsertSource(mapInstance, LINE_SOURCE, line);
    if (!mapInstance.getLayer(LINE_LAYER)) {
      try {
        mapInstance.addLayer({
          id: LINE_LAYER,
          type: 'line',
          source: LINE_SOURCE,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#ED107D', 'line-width': 2, 'line-opacity': 0.55 },
        });
      } catch (e) {
        console.error('Error adding path layer:', e);
      }
    }
  }

  private upsertSource(map: Map, id: string, data: GeoJSON.GeoJSON) {
    try {
      const existing = map.getSource(id);
      if (existing) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (existing as any).setData(data);
      } else {
        map.addSource(id, { type: 'geojson', data });
      }
    } catch (e) {
      console.error('Error updating source %s:', id, e);
    }
  }

  playPauseAnimation() {
    if (this.animationStateService.animationState() === 'playing') {
      this.mapAnimationService.pauseAnimation();
    } else {
      if (this.animationStateService.animationState() === 'stopped') {
        // Points hide during playback so the moving dot reads; they come back when it ends.
        this.pointsBeforePlay = this.showAllPositions();
      }
      this.selectedPosition.set(null);
      this.showAllPositions.set(false);
      this.removePointsLayer();
      this.mapAnimationService.startAnimation(this.positionsWithColor(), this.animationsStep());
    }
  }

  stopAnimation() {
    // The service emits animationCompleted after stopping, which restores the points.
    this.mapAnimationService.stopAnimation();
  }

  setSpeed(speed: number) {
    this.animationsStep.set(speed);
    if (this.animationStateService.animationState() === 'playing') {
      this.mapAnimationService.startAnimation(this.positionsWithColor(), this.animationsStep());
    }
  }

  togglePositions(show: boolean) {
    this.showAllPositions.set(show);
    if (show) {
      setTimeout(() => this.forceShowAllPositions(), 0);
    } else {
      this.removePointsLayer();
    }
  }

  private removePointsLayer() {
    const map = this.map();
    if (map && map.getLayer(POINTS_LAYER)) {
      try {
        map.removeLayer(POINTS_LAYER);
      } catch (e) {
        console.error('Error removing points layer:', e);
      }
    }
  }

  forceShowAllPositions() {
    const map = this.map();
    if (!map) return;
    this.removePointsLayer();
    this.setupGeoJsonSource(map);
    try {
      map.addLayer({
        id: POINTS_LAYER,
        type: 'circle',
        source: POINTS_SOURCE,
        paint: HISTORY_POINTS_PAINT,
      });
    } catch (e) {
      console.error('Error adding all positions to map:', e);
    }
  }

  closeDialog() {
    this.setSpeed(100);
    if (this.animationStateService.animationState() !== 'stopped') {
      this.mapAnimationService.stopAnimation();
    }
    this.showAllPositions.set(true);
    this.selectedPosition.set(null);
    setTimeout(() => this.dialog().hide(), 10);
  }

  selectTracker(p: PositionWithColor) {
    this.selectedPosition.set(p);
    this.mapStateService.flyTo(p.longitude!, p.latitude!);
  }

  protected selectNeighbour(delta: 1 | -1) {
    const idx = this.selectedIndex();
    const next = this.positionsWithColor()[idx + delta];
    if (next) this.selectTracker(next);
  }

  onTimeRangeChanged(range: { start: number; end: number }) {
    this.timeFilter.set(range);
    this.selectedPosition.set(null);
    requestAnimationFrame(() => {
      if (this.controlsComponent) {
        this.controlsComponent.updateFilteredCount(this.positionsWithColor().length);
      }
      const map = this.map();
      if (map) {
        this.setupGeoJsonSource(map);
        if (this.showAllPositions()) {
          this.forceShowAllPositions();
        }
      }
    });
  }

  private generateColorFromIndex(index: number, total: number): string {
    const startHue = 240;
    const endHue = 60;
    const hue = total > 1 ? Math.round(startHue + ((endHue - startHue) * index) / (total - 1)) : startHue;
    return `hsl(${hue},100%,50%)`;
  }
}
